import React from "react";
import { Plus, Minus } from "lucide-react";
import { useMediaQuery } from "../hooks/use-media-query";
import { formatINR } from "../lib/utils.fmt";
import { useCart } from "../lib/cart";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter,
} from "./ui/drawer";

/**
 * Product-card variant picker. Renders as a Dialog on Web/Desktop/iPad
 * (viewport >= 768px, Tailwind's `md` breakpoint) and a bottom Drawer on
 * phone-sized viewports (Android/iOS/mobile web) — the standard shadcn
 * "responsive dialog" pattern, driven by useMediaQuery rather than
 * Capacitor platform detection so it also behaves correctly for mobile
 * web and iPad browsers, not just the native app shells.
 *
 * Every row manages its own cart line LIVE (+/- adjusts that variant's
 * quantity immediately, no separate "confirm" step) — self-contained via
 * useCart(), so this is the one place variant+cart logic lives; the
 * product card itself only needs to open/close it.
 */
export default function VariantPickerSheet({ open, onOpenChange, product, variants, primary }) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { items, add, setQty } = useCart();

  const qtyFor = (variantId) => items.find((x) => x.id === product.id && x.variant_id === variantId)?.qty || 0;
  const lineIdFor = (variantId) => `${product.id}::${variantId}`;

  const list = (
    <div className="space-y-2 px-4 pb-2 overflow-y-auto" data-testid="variant-sheet-list">
      {variants.map((v) => {
        const oos = v.is_in_stock === false;
        const qty = qtyFor(v.id);
        return (
          <div
            key={v.id}
            className={`w-full flex items-center justify-between gap-3 p-3 rounded-2xl border-2 transition-colors ${
              qty > 0 ? "border-transparent bg-brand-cream" : "border-neutral-200 bg-white"
            } ${oos ? "opacity-50" : ""}`}
            data-testid={`variant-option-${v.id}`}
          >
            <div className="min-w-0">
              <div className="font-bold text-sm truncate">{v.label || v.pack_size}</div>
              <div className="text-xs mt-0.5 text-neutral-500">
                {v.effective_price != null && v.effective_price < v.price ? (
                  <>
                    <span className="text-brand-leaf font-bold">{formatINR(v.effective_price)}</span>
                    <span className="line-through ml-1.5">{formatINR(v.price)}</span>
                  </>
                ) : (
                  <>
                    {formatINR(v.price)}
                    {v.mrp > v.price && <span className="line-through ml-1.5">{formatINR(v.mrp)}</span>}
                  </>
                )}
              </div>
            </div>
            <div className="shrink-0">
              {oos ? (
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-brand-clay">Out of stock</span>
              ) : qty === 0 ? (
                <button
                  type="button"
                  onClick={() => add(product, 1, v)}
                  className="w-9 h-9 rounded-xl text-white inline-flex items-center justify-center"
                  style={{ background: primary }}
                  data-testid={`variant-add-${v.id}`}
                  aria-label={`Add ${v.label || v.pack_size}`}
                >
                  <Plus className="w-4 h-4" />
                </button>
              ) : (
                <div className="h-9 rounded-xl flex items-center gap-1 px-1 text-white" style={{ background: primary }}>
                  <button
                    type="button"
                    onClick={() => setQty(lineIdFor(v.id), qty - 1)}
                    className="w-7 h-7 rounded-lg bg-white/20 inline-flex items-center justify-center"
                    data-testid={`variant-minus-${v.id}`}
                    aria-label={`Decrease ${v.label || v.pack_size}`}
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="font-mono font-extrabold text-sm w-5 text-center" data-testid={`variant-qty-${v.id}`}>{qty}</span>
                  <button
                    type="button"
                    onClick={() => setQty(lineIdFor(v.id), qty + 1)}
                    className="w-7 h-7 rounded-lg bg-white/20 inline-flex items-center justify-center"
                    data-testid={`variant-plus-${v.id}`}
                    aria-label={`Increase ${v.label || v.pack_size}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm" data-testid="variant-dialog">
          <DialogHeader>
            <DialogTitle className="line-clamp-2">{product.name}</DialogTitle>
          </DialogHeader>
          {list}
          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)} className="w-full text-white" style={{ background: primary }} data-testid="variant-sheet-done">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="variant-drawer">
        <DrawerHeader>
          <DrawerTitle className="line-clamp-2">{product.name}</DrawerTitle>
        </DrawerHeader>
        {list}
        <DrawerFooter>
          <Button type="button" onClick={() => onOpenChange(false)} className="text-white h-12 rounded-2xl" style={{ background: primary }} data-testid="variant-sheet-done">
            Done
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
